import { Link } from "react-router-dom";
import { ArrowRight, Users, Radio, Smartphone, ListPlus, ChevronUp, Mic, Heart, Bookmark, Compass } from "lucide-react";
import PhoneMockup from "../components/PhoneMockup";
import Waveform from "../components/Waveform";
import { Reveal, MaskLine } from "../components/Reveal";

const STEPS = [
  {
    n: "01",
    title: "Tap into a room",
    copy: "Start a public room for the night, or a private one behind a code. Your crowd joins in a tap — no accounts, no setup.",
    bullets: [
      { icon: Users, text: "Public or invite-only" },
      { icon: Radio, text: "Follow hosts you trust" },
      { icon: Smartphone, text: "Join from any device" },
    ],
    variant: "live",
  },
  {
    n: "02",
    title: "Build the queue together",
    copy: "Everyone adds tracks, upvotes move the good ones up, and the DJ pass keeps the aux fair. The room sounds like the room.",
    bullets: [
      { icon: ListPlus, text: "Everyone adds tracks" },
      { icon: ChevronUp, text: "Upvotes decide what's next" },
      { icon: Mic, text: "Pass the aux fairly" },
    ],
    variant: "queue",
  },
  {
    n: "03",
    title: "React & discover",
    copy: "Reactions land right on the beat. Save anything you hear to your library, and follow the people with the taste you trust.",
    bullets: [
      { icon: Heart, text: "Live reactions on the beat" },
      { icon: Bookmark, text: "Save songs as they play" },
      { icon: Compass, text: "Follow tastemakers" },
    ],
    variant: "discover",
  },
];

function Step({ step, index }) {
  const flip = index % 2 === 1;
  return (
    <section className="section !py-16 lg:!py-24" data-testid={`step-${step.n}`}>
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal className={flip ? "lg:order-2" : ""}>
          <p className="step-index">{step.n}</p>
          <h2 className="section-title mt-5">{step.title}</h2>
          <p className="body-lg mt-5 max-w-md">{step.copy}</p>
          <ul className="mt-8 space-y-3.5">
            {step.bullets.map((b) => (
              <li key={b.text} className="flex items-center gap-3.5">
                <span className="icon-chip !w-10 !h-10 !rounded-xl"><b.icon size={17} /></span>
                <span className="text-sm font-medium">{b.text}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.12} className={`flex justify-center ${flip ? "lg:order-1" : ""}`}>
          <div className="relative">
            <div className="glow-blob" style={{ width: 320, height: 320, top: "10%", left: "50%", transform: "translateX(-50%)" }} aria-hidden="true" />
            <PhoneMockup variant={step.variant} className="relative" />
          </div>
        </Reveal>
      </div>
      {index < STEPS.length - 1 && (
        <div className="mx-auto max-w-6xl px-5 sm:px-8 mt-16 lg:mt-24">
          <Waveform bars={56} className="h-10 opacity-50" testid={`waveform-divider-${step.n}`} />
        </div>
      )}
    </section>
  );
}

export default function HowItWorks() {
  return (
    <>
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-10" data-testid="howitworks-hero">
        <div className="glow-blob" style={{ width: 560, height: 560, top: "-30%", right: "-10%" }} aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <p className="eyebrow" data-testid="howitworks-eyebrow">
            <span className="live-dot" /> How it works
          </p>
          <h1 className="hero-title mt-6 !text-[clamp(2rem,4.6vw,3.4rem)]" data-testid="howitworks-title">
            <MaskLine delay={0.05}>Three taps to</MaskLine>
            <MaskLine delay={0.18}>
              a <span className="text-gradient">full house.</span>
            </MaskLine>
          </h1>
          <p className="body-lg max-w-xl mt-6">
            Tap In is built around one idea: music lands differently when you hear it together.
            Here's the whole thing — start to last track.
          </p>
        </div>
      </section>
      {STEPS.map((s, i) => (
        <Step key={s.n} step={s} index={i} />
      ))}
      <section className="section !pt-0">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <div className="cta-panel rounded-[2.5rem] border border-grape/25 px-6 py-14 sm:px-16 text-center" data-testid="howitworks-cta">
              <h2 className="section-title">Ready when you are.</h2>
              <p className="body-lg mt-4">Grab early access and host your first room at launch.</p>
              <Link to="/access" data-testid="howitworks-cta-button" className="btn-primary mt-8">
                Get early access <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
